import React, { useState, useRef, useEffect } from "react";
import {
  MapContainer,
  TileLayer,
  Polyline,
  CircleMarker,
  Tooltip,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-control-geocoder/dist/Control.Geocoder.css";

import SoilModal from "./SoilModal";
import ProcessFile from "./NewProcessFile";
import L from "leaflet";
import Legend from "./Legend";
import { LuUpload } from "react-icons/lu";
import { FaTools } from "react-icons/fa"; // Maintenance icon
import { useCachedFetch } from "../../utilityhook/useCachedFetch";
import { urls } from "../../services/apiService";
import { checkPageStatus } from "../../utilityhook/pagecheck"; // <-- maintenance util

const pakistanCoordinates = [33.6844, 73.0479];

// ──────────────────────────────────────────────────────────────
// Cache helpers (unchanged)
const manageHeatmapCache = {
  get: (deviceId, date, attribute) => {
    const cacheKey = `heatmap_${deviceId}_${date}_${attribute}`;
    const cached = localStorage.getItem(cacheKey);
    return cached ? JSON.parse(cached) : null;
  },
  set: (deviceId, date, attribute, data) => {
    const cacheKey = `heatmap_${deviceId}_${date}_${attribute}`;
    localStorage.setItem(cacheKey, JSON.stringify(data));
  },
  cleanup: (maxEntries = 1) => {
    const prefix = "heatmap_";
    const keys = Object.keys(localStorage)
      .filter((k) => k.startsWith(prefix))
      .sort(
        (a, b) =>
          (JSON.parse(localStorage.getItem(a))?.timestamp || 0) -
          (JSON.parse(localStorage.getItem(b))?.timestamp || 0)
      );

    if (keys.length > maxEntries) {
      keys.forEach((k) => localStorage.removeItem(k));
    }
  },
};

const SoilAnalyse = () => {
  // ────── State ──────
  const [mapCenter] = useState(pakistanCoordinates);
  const [imageSelector, setOpenImgSelecter] = useState(false);
  const [coord, setCoord] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedAttribute, setSelectedAttribute] = useState("pH");
  const [polylineData, setPolylineData] = useState([]);
  const [markerData, setMarkerData] = useState([]);
  const [isActive, setIsActive] = useState(true); // <-- maintenance flag

  const mapRef = useRef(null);

  // ────── Data fetching ──────
  const { data: savedFields } = useCachedFetch(urls.devicesDates, "soilDevices");

  const { data: availableDates } = useCachedFetch(
    selectedDevice ? `${urls.devicesDates}/${selectedDevice}/dates` : null,
    `soilDates_${selectedDevice}`,
    [selectedDevice],
    selectedAttribute
  );

  // ────── Maintenance check (once) ──────
useEffect(() => {
  const fetchPageStatus = async () => {
    const active = await checkPageStatus("6-soil-analysis"); // ✅ wait for backend or localStorage
    setIsActive(active);
  };
  fetchPageStatus();
}, []);

  // ────── Color mapping (unchanged) ──────
  const colorMapping = {
    conductivity: [
      { color: "beige", label: "Very Low (0 - 200)" },
      { color: "purple", label: "Low (200.1 - 404)" },
      { color: "orange", label: "Medium (405 - 800)" },
      { color: "darkorange", label: "High (801 - 1600)" },
      { color: "red", label: "Very High (1600+)" },
    ],
    nitrogen: [
      { color: "lightyellow", label: "Very Low (0 - 10.99)" },
      { color: "lightgreen", label: "Low (11 - 20.99)" },
      { color: "green", label: "Medium (21 - 40)" },
      { color: "darkgreen", label: "High (40+)" },
    ],
    phosphor: [
      { color: "lightyellow", label: "Very Low (0 - 10.99)" },
      { color: "lightblue", label: "Low (11 - 20.99)" },
      { color: "blue", label: "Medium (21 - 40)" },
      { color: "darkblue", label: "High (40+)" },
    ],
    pH: [
      { color: "beige", label: "Very Low (0 - 200)" },
      { color: "purple", label: "Low (200.1 - 404)" },
      { color: "orange", label: "Medium (405 - 800)" },
      { color: "darkorange", label: "High (801 - 1600)" },
      { color: "red", label: "Very High (1600+)" },
    ],
    moisture: [
      { color: "lightcyan", label: "Very Low (0 - 15)" },
      { color: "cyan", label: "Low (15.1 - 30.99)" },
      { color: "lightblue", label: "Medium (31 - 60.99)" },
      { color: "blue", label: "High (61 - 80.99)" },
      { color: "darkblue", label: "Very High (81 - 100)" },
    ],
    potassium: [
      { color: "white", label: "Very Low (0 - 52.99)" },
      { color: "peachpuff", label: "Low (53 - 85)" },
      { color: "orange", label: "Medium (86 - 120)" },
      { color: "red", label: "High (121 - 155)" },
      { color: "darkred", label: "Very High (155+)" },
    ],
  };

  // ────── Heatmap fetching ──────
  const fetchHeatmapData = async () => {
    if (!selectedDevice || !selectedDate || !selectedAttribute) return;

    const cached = manageHeatmapCache.get(
      selectedDevice,
      selectedDate,
      selectedAttribute
    );
    if (cached) {
      updateMapData(cached.coordinates, cached.markers);
      return;
    }

    try {
      const resp = await fetch(urls.get_heatmap_data, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          device_id: selectedDevice,
          date: selectedDate,
          attribute: selectedAttribute,
        }),
      });
      const result = await resp.json();

      if (result.error) {
        console.error("Heatmap error:", result.error);
        return;
      }

      const coordinates = result.all_coordinates;
      const markers = result.data.map((p) => ({
        coordinates: p.coordinates,
        color: p.color,
        value: p.value,
      }));

      manageHeatmapCache.set(
        selectedDevice,
        selectedDate,
        selectedAttribute,
        { coordinates, markers, timestamp: Date.now() }
      );
      manageHeatmapCache.cleanup();

      updateMapData(coordinates, markers);
    } catch (e) {
      console.error(e);
    }
  };

  const updateMapData = (coordinates, markers) => {
    setPolylineData(coordinates);
    setMarkerData(markers);
    setCoord(coordinates);

    if (mapRef.current && coordinates.length) {
      const bounds = L.latLngBounds(coordinates);
      mapRef.current.fitBounds(bounds);
    }
  };

  // ────── Effects ──────
  useEffect(() => {
    fetchHeatmapData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAttribute, selectedDevice, selectedDate]);

  useEffect(() => {
    if (coord.length && mapRef.current) {
      const bounds = L.latLngBounds(coord);
      mapRef.current.fitBounds(bounds);
    }
  }, [coord]);

  // ────── Handlers ──────
  const handleDeviceSelect = (e) => {
    setSelectedDevice(e.target.value);
    setSelectedDate("");
  };

  const handleDateSelect = (e) => setSelectedDate(e.target.value);
  const bond = (points) => setCoord(points);

  // ────── Render ──────
  return (
    <div className="w-full lg:w-[96%] h-screen overflow-hidden pl-[1vw] absolute right-0">
      {/* Header */}
      <div className="flex md:pl-5 flex-wrap items-center justify-center md:justify-between w-full gap-2">
        <h1 className="text-sm mt-2 sm:text-base md:text-lg lg:text-xl font-medium">
          <p className="bg-[#dbeafe] rounded-md w-full max-w-[250px] sm:max-w-[300px] md:max-w-[350px] lg:max-w-[400px] px-4 py-2 flex items-center justify-center text-center">
            <span className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold">
              Soil Health Monitoring
            </span>
          </p>
        </h1>

        <div className="flex flex-col p-3 md:flex-row items-center gap-2 sm:gap-3 justify-between w-full lg:w-[35%]">
          <div className="w-full flex items-end justify-end gap-2">
<div className="flex items-center gap-2">
  {isActive ? (
    // ✅ Unlocked dropdown when not in maintenance
    <div>
      <select
        className="min-w-fit w-1/2 bg-green-500 text-white font-medium py-2 px-2 rounded-md cursor-pointer hover:bg-green-600 duration-300"
        onChange={handleDeviceSelect}
        value={selectedDevice || ""}
      >
        <option value="">Select a device</option>
        {savedFields?.devices?.map((deviceId) => (
          <option key={deviceId} value={deviceId}>
            {deviceId}
          </option>
        ))}
      </select>
    </div>
  ) : (
    // 🔒 Locked view when in maintenance
    <select
      className="min-w-fit w-1/2 bg-gray-400 text-white font-medium py-2 px-2 rounded-md cursor-not-allowed opacity-70"
      value=""
      disabled
    >
      <option>Device selection locked</option>
    </select>
  )}
</div>


            {selectedDevice && (
              <select
                className="min-w-fit w-1/2 bg-green-500 text-white font-medium py-2 px-2 rounded-md cursor-pointer hover:bg-green-600 duration-300"
                onChange={handleDateSelect}
                value={selectedDate}
              >
                <option value="">Select a date</option>
                {availableDates?.dates?.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>

      {/* Main layout */}
      <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center h-[90vh] w-[95vw]">
        {/* Sidebar */}
        <div className="flex lg:pl-5 flex-col items-center justify-start lg:gap-2 h-[45vh] lg:h-[93vh] w-full lg:w-[25%]">
          <SoilModal
            selectedData={{ selectedDate, selectedDevice }}
            setSelectedParameter={setSelectedAttribute}
            selectedAttribute={selectedAttribute}
            polylineData={polylineData}
          />
        </div>

        {/* Map */}
        <div className="h-[60vh] lg:h-[90vh] w-full lg:w-[70vw] overflow-hidden rounded-md shadow-md shadow-black relative">
          <div className="flex h-full w-full">
            <MapContainer
              center={mapCenter}
              zoom={8}
              className="w-full h-full"
              ref={mapRef}
            >
              <TileLayer url="http://mt0.google.com/vt/lyrs=y&hl=en&x={x}&y={y}&z={z}&s=Ga" />

              {/* Only render map layers when page is active */}
              {isActive && (
                <>
                  {polylineData.length > 0 && (
                    <Legend
                      selectedParameter={selectedAttribute}
                      colorMapping={colorMapping}
                    />
                  )}

                  {polylineData.length > 0 && (
                    <Polyline positions={polylineData} color="yellow" opacity={1}>
                      <Tooltip sticky>Convex Hull Boundary</Tooltip>
                    </Polyline>
                  )}

                  {markerData.map((m, i) => (
                    <CircleMarker
                      key={`${m.coordinates[0]}-${m.coordinates[1]}-${m.value}`}
                      center={m.coordinates}
                      color={m.color}
                      radius={5}
                      fillOpacity={0.7}
                    >
                      <Tooltip sticky>{`${selectedAttribute}: ${m.value}`}</Tooltip>
                    </CircleMarker>
                  ))}
                </>
              )}

              {/* Upload button (always visible) */}
              <div className="relative">
                <div
                  className="absolute btn btn-circle top-4 left-14 z-[500] group cursor-pointer"
                  onClick={() => setOpenImgSelecter(true)}
                >
                  <LuUpload size={25} />
                  <span className="absolute left-[3vw] w-32 top-1/2 -translate-y-1/2 bg-black text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    Upload your file
                  </span>
                </div>
              </div>
            </MapContainer>

            {/* Maintenance overlay – only when NOT active */}
            {!isActive && (
              <div className="absolute inset-0 bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300 bg-opacity-90 flex flex-col items-center justify-center text-center z-[9999] backdrop-blur-sm pointer-events-none">
                <FaTools className="text-5xl text-green-500 mb-4 animate-bounce" />
                <h1 className="text-3xl md:text-5xl font-semibold text-gray-600 mb-2">
                  Page Under Maintenance
                </h1>
                <p className="text-gray-500 text-lg max-w-md px-4">
                  We’re currently updating this feature to improve your
                  experience. Please check back soon!
                </p>
                <div className="mt-6">
                  <div className="w-16 h-16 border-4 border-gray-600 border-t-green-500 rounded-full animate-spin mx-auto"></div>
                </div>
              </div>
            )}
          </div>

          {/* File-upload modal (works even in maintenance mode) */}
          {imageSelector && (
            <ProcessFile setOpenImgSelecter={setOpenImgSelecter} bound={bond} />
          )}
        </div>
      </div>
    </div>
  );
};

export default SoilAnalyse;